import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title";
import { useTETranslation } from "@/hooks/app/useTETranslation";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import { thunks } from "@/lib/redux/thunks";
import { getOrderDetail } from "@/utils/booking/getOrderDetail";
import { parseJson } from "@/utils/safeParse/parseJson";
;
import { Alert, Col, Divider, Form, List, Modal, Row, Select, Tabs } from "antd"
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react"
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

export type ModalCancelBookingHotelRef = {
  open: () => void,
  close: () => void,
}

const cancelReasons = [
  "hotel.cancellation_reason_1",
  "hotel.cancellation_reason_2",
  "hotel.cancellation_reason_3",
  "hotel.cancellation_reason_4",
  "hotel.cancellation_reason_5",
  "hotel.cancellation_reason_6",
  "hotel.cancellation_reason_7",
  "hotel.cancellation_reason_8",
  "hotel.cancellation_reason_9",
  "hotel.cancellation_reason_10",
  "hotel.cancellation_reason_11",
  "hotel.cancellation_reason_12",
  "hotel.cancellation_reason_13",
];

const ModalCancelBookingHotel = forwardRef<ModalCancelBookingHotelRef, any>(
  (props, ref) => {
    const [modal, modalCtx] = Modal.useModal()
    const cancelOrder = useAppSelector(state => state.hotel.hotels_booking_cancel)
    const confirmCancelOrder = useAppSelector(state => state.hotel.hotels_booking_confirm_cancel)
    const order = useAppSelector(state => state.booking.order_detail.data)
    const [open, setOpen] = useState<boolean>(false)
    const [tab, setTab] = useState<'confirm-cancel' | 'cancel-info'>('confirm-cancel')
    const handleClose = useCallback(() => {
      setOpen(false)
    }, [])
    const { getFormatCurrency } = useTETranslation()
    const { t } = useTranslation()

    const metaData = useMemo<any>(() => {
      return parseJson(order?.metaData) || {}
    }, [order?.metaData])

    const dispatch = useAppDispatch()

    const handleOpen = useCallback(() => {
      setOpen(true)
    }, [])

    useImperativeHandle(ref, () => {
      return {
        open: handleOpen,
        close: handleClose
      }
    }, [handleClose, handleOpen])

    return (
      <Modal
        title={t("hotel.cancellation_title")}
        open={open}
        onCancel={handleClose}
        width={700}
        centered
        cancelText={t("hotel.cancellation_continue")}
        okText={tab === 'confirm-cancel' ? t("hotel.cancellation") : t("hotel.cancellation_confirm")}
        okButtonProps={{
          danger: true
        }}
        confirmLoading={cancelOrder.loading || confirmCancelOrder.loading}
        onOk={async () => {
          if (tab === 'confirm-cancel') {
            try {
              await dispatch(
                thunks.hotels_booking_cancel({
                  body: {
                    TYPE: "JSON",
                    bookingId: metaData?.bookingResponse?.bookingDetails[0]?.id,
                  },
                })
              ).unwrap()
            } catch {
              toast.error(t("booking.fetching_cancellation_hotel_detail_failure"))
              return
            }
            setTab('cancel-info')
          } else {
            let res
            try {
              res = await dispatch(
                thunks.hotels_booking_confirm_cancel({
                  body: {
                    TYPE: "JSON",
                    orderCode: order?.orderCode,
                    bookingId: metaData?.bookingResponse?.bookingDetails[0]?.id,
                    reference: Number(cancelOrder?.data?.cancellationSummary?.reference || 0),
                    // reason: cancelReason,
                  },
                })
              ).unwrap()
            } catch {
              res = null
            }
            getOrderDetail({ orderCode: order?.orderCode })
            if (res?.isSuccess) {
              modal.success({
                title: t("common.notification"),
                content: t("hotel.cancellation_success"),
                okText: t("common.confirm")
              })

              handleClose()
            } else {
              modal.error({
                title: t("common.notification"),
                content: t("hotel.cancellation_fail"),
                okText: t("common.confirm")
              })
            }
          }
        }}
      >
        {modalCtx}
        <Tabs
          renderTabBar={() => <></>}
          activeKey={tab}

          items={[
            {
              key: "confirm-cancel",
              tabKey: "confirm-cancel",
              label: "Confirm cancel",
              children: (
                <Form layout='vertical'>
                  <Row style={{ marginBottom: '1rem' }}>
                    <Text>
                      {t("hotel.cancellation_confirm_desc")}
                    </Text>
                  </Row>
                  <Form.Item
                    label={t("hotel.cancellation_reason")}
                  >
                    <Select
                      placeholder={t("hotel.cancellation_reason_label")}
                      options={cancelReasons.map((reason) => ({
                        value: reason,
                        label: t(reason),
                      }))}
                      popupMatchSelectWidth
                    />
                  </Form.Item>
                </Form>
              )
            },
            {
              key: "cancel-info",
              tabKey: "cancel-info",
              label: "Cancel info",
              children: (
                <>
                  <Row style={{ marginBottom: '1rem' }}>
                    <Text>
                      {(cancelOrder?.data?.cancellationSummary as any)?.policyDetails ||
                        t("hotel.cancellation_info_desc")}
                    </Text>
                  </Row>
                  {!cancelOrder?.data?.cancellationSummary?.refundRate?.[0]?.inclusive && (
                    <Row style={{ marginBottom: '1rem' }}>
                      <Col span={24}>
                        <Alert
                          showIcon
                          type='warning'
                          title={t("booking.hotel_norefund_message")}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row justify="space-between">
                    <Col span={24}>
                      <List
                        header={t("hotel.cancellation_info_summary")}
                        bordered
                        size='small'
                        dataSource={[
                          {
                            label: t("hotel.cancellation_fee"),
                            value: getFormatCurrency(
                              (cancelOrder?.data?.order?.feePayment || 0) +
                              ((cancelOrder?.data?.cancellationSummary?.paymentRate?.[0]?.inclusive || 0) - (cancelOrder?.data?.cancellationSummary?.refundRate?.[0]?.inclusive || 0))
                            ),
                          },
                          {
                            label: t("hotel.cancellation_refund_amount"),
                            value: getFormatCurrency(
                              (cancelOrder?.data?.order?.totalAmount || 0) -
                              (cancelOrder?.data?.order?.feePayment || 0) - 
                              ((cancelOrder?.data?.cancellationSummary?.paymentRate?.[0]?.inclusive || 0) - (cancelOrder?.data?.cancellationSummary?.refundRate?.[0]?.inclusive || 0))
                            )
                          }
                        ]}
                        renderItem={({ label, value }) => {
                          return (
                            <List.Item>
                              <List.Item.Meta
                                title={label}
                              />
                              <div>
                                {value}
                              </div>
                            </List.Item>
                          )
                        }}
                      />
                    </Col>
                  </Row>
                  {/* <div className={styles.cancellationSummarySection}>
                  <Title level={5}>Tóm tắt hủy đặt phòng</Title>

                  <Row className={styles.summaryRow} justify="space-between">
                    <Col>
                      <Text strong>Phí hủy đặt phòng:</Text>
                    </Col>
                    <Col>
                      <Text className={styles.feeAmount}>
                        {formatCurrencyValue(
                          cancellationSummary?.paymentRate?.[0]?.inclusive || 0
                        )}
                      </Text>
                    </Col>
                  </Row>
                  <Row className={styles.summaryRow} justify="space-between">
                    <Col>
                      <Text strong>Số tiền hoàn trả:</Text>
                    </Col>
                    <Col>
                      <Text className={styles.refundAmount}>
                        {formatCurrencyValue(
                          cancellationSummary?.refundRate?.[0]?.inclusive || 0
                        )}
                      </Text>
                    </Col>
                  </Row>

                  <Divider className={styles.cancellationDivider} />                <div className={styles.cancellationPolicy}>
                    <Text>
                      {cancellationSummary?.policyDetails ||
                        "Bằng cách xác nhận hủy đặt phòng này, bạn đồng ý với các điều khoản hủy đặt phòng như được nêu trong đặt phòng của bạn."}
                    </Text>
                  </div>
                </div>              <div className={styles.cancelModalActions}>
                  <Button
                    onClick={handleConfirmCancellation}
                    className={styles.confirmCancelButton}
                    danger
                    loading={confirmLoading}
                  >
                    Xác nhận hủy đặt phòng
                  </Button>

                  <Button
                    type="primary"
                    onClick={handleCloseCancelModal}
                    className={styles.keepBookingButton}
                  >
                    Giữ đặt phòng của tôi
                  </Button>
                </div> */}
                </>
              )
            }
          ]}
        />

      </Modal>
    )
  }
)

export default ModalCancelBookingHotel